import { Download, Eye, FileText } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { DocumentRequestsTab } from '@/components/document-requests/document-requests-tab'
import { useDocuments } from '@/hooks/use-documents'
import { useTabQueryParam } from '@/hooks/use-tab-query-param'
import { downloadDocument, isViewableMimeType, viewDocument } from '@/lib/documents'
import { formatDate } from '@/lib/utils'
import { cn } from '@/lib/utils'
import type { DocumentCategory } from '@riverpark/types'

const CATEGORY_LABEL: Record<DocumentCategory, string> = {
  agm_minutes: 'AGM minutes',
  financial_report: 'Financial report',
  notice: 'Notice',
  title_deed: 'Title deed',
  compliance: 'Compliance',
  other: 'Other',
}

type Tab = 'library' | 'requests'
const TABS: { id: Tab; label: string }[] = [
  { id: 'library', label: 'Library' },
  { id: 'requests', label: 'Requests' },
]
const VALID_TABS: Tab[] = ['library', 'requests']

function DocumentLibrary() {
  const { data, isLoading } = useDocuments()
  const items = data?.data ?? []

  if (isLoading) return <p className="font-sans text-sm text-muted-foreground">Loading…</p>
  if (items.length === 0) {
    return (
      <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
        No documents available yet.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {items.map((doc) => (
        <div
          key={doc.id}
          className="flex items-center justify-between gap-3 rounded border border-border-2 bg-card p-3"
        >
          <div className="flex min-w-0 items-start gap-3">
            <FileText size={18} className="mt-0.5 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <p className="truncate font-sans text-sm font-medium text-ink">{doc.title}</p>
              <p className="mt-0.5 font-sans text-xs text-muted-foreground">
                {CATEGORY_LABEL[doc.category]} · {formatDate(new Date(doc.createdAt * 1000))}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {isViewableMimeType(doc.mimeType) && (
              <button
                type="button"
                onClick={() => viewDocument(doc)}
                className="flex items-center gap-1.5 rounded border border-border-2 px-3 py-1.5 font-sans text-xs font-medium text-ink transition-colors hover:bg-cream-2/50"
              >
                <Eye size={14} />
                View
              </button>
            )}
            <button
              type="button"
              onClick={() => downloadDocument(doc)}
              className="flex items-center gap-1.5 rounded border border-border-2 px-3 py-1.5 font-sans text-xs font-medium text-ink transition-colors hover:bg-cream-2/50"
            >
              <Download size={14} />
              Download
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

export function MemberDocuments() {
  const [tab, setTab] = useTabQueryParam(VALID_TABS, 'library')

  return (
    <PageShell>
      <PageHeader
        label="Estate"
        title="Documents"
        description="AGM minutes, notices, estate documents, and compliance requests."
      />

      <div className="mb-6 flex gap-1 border-b border-border-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              'px-4 py-2.5 font-sans text-sm font-medium transition-colors',
              tab === t.id
                ? 'border-b-2 border-primary text-ink'
                : 'text-muted-foreground hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'library' ? <DocumentLibrary /> : <DocumentRequestsTab />}
    </PageShell>
  )
}
